// frontend/src/samkalp-admin/tabs/StudentsTab.js
//
// Duplicated from frontend/src/pages/AskOSAdmin.js's StudentsTab (not
// exported there — see plan for why duplication is the confirmed-safe
// reuse path). Uses the shared `api` client directly (no admin_token
// header needed — this dashboard's login already writes access_token,
// same as Studio).
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Form, Button, Alert, Badge, Spinner, Modal, InputGroup } from 'react-bootstrap';
import { Users, Plus, Trash2, KeyRound, Pencil, Search, Unlink } from 'lucide-react';
import api from '../../api/api';
import { StatusBadge, EmptyRow, useAlert, UInput } from './shared';

const StudentsTab = ({ slug }) => {
    const [students, setStudents] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [search, setSearch]     = useState('');
    const [modal, setModal]       = useState(null);
    const [selected, setSelected] = useState(null);
    const [form, setForm]         = useState({});
    const [saving, setSaving]     = useState(false);
    const [alert, showAlert]      = useAlert();

    const LANGS = [
        { value: 'en', label: 'English' },
        { value: 'ta', label: 'Tamil'   },
        { value: 'hi', label: 'Hindi'   },
        { value: 'te', label: 'Telugu'  },
        { value: 'ml', label: 'Malayalam' },
        { value: 'kn', label: 'Kannada' },
    ];
    const VOICES = [
        { value: 'nova',    label: 'Nova (Female, warm)'   },
        { value: 'shimmer', label: 'Shimmer (Female, clear)' },
        { value: 'alloy',   label: 'Alloy (Neutral)'       },
        { value: 'echo',    label: 'Echo (Male)'           },
    ];

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`/tenant/${slug}/askos/admin/students`);
            setStudents(res.data);
        } catch { setStudents([]); }
        finally { setLoading(false); }
    }, [slug]);

    useEffect(() => { load(); }, [load]);

    const filtered = students.filter(s =>
        `${s.email} ${s.full_name || ''}`.toLowerCase().includes(search.toLowerCase())
    );

    const save = async () => {
        setSaving(true);
        try {
            if (modal === 'add') {
                await api.post(`/tenant/${slug}/askos/register`, form);
                showAlert('Student account created successfully.');
           } else if (modal === 'edit') {
                const payload = {
                    full_name: form.full_name || null,
                    language_preference: form.language_preference || 'en',
                    voice_preference: form.voice_preference || 'nova',
                    telegram_id: form.telegram_id || null,
                    telegram_username: form.telegram_username || null,
                    phone_number: form.phone_number || null,
                };
                await api.patch(`/tenant/${slug}/askos/admin/students/${selected.id}`, payload);
                showAlert('Student profile updated.');
            } else if (modal === 'reset') {
                await api.patch(`/tenant/${slug}/askos/admin/students/${selected.id}/reset-password`, { password: form.password });
                showAlert('Password has been reset.');
            } else if (modal === 'delete') {
                await api.delete(`/tenant/${slug}/askos/admin/students/${selected.id}`);
                showAlert('Student deactivated.', 'warning');
            }
            setModal(null);
            load();
        } catch (e) {
            showAlert(e.response?.data?.detail || 'Action failed. Please try again.', 'danger');
        } finally {
            setSaving(false);
        }
    };

    const ModalFooter = ({ confirmLabel, confirmVariant = 'primary' }) => (
        <Modal.Footer className="border-0 pt-0">
            <Button variant="light" onClick={() => setModal(null)}>Cancel</Button>
            <Button variant={confirmVariant} onClick={save} disabled={saving} className="rounded-pill px-4">
                {saving ? <Spinner animation="border" size="sm" aria-hidden="true" /> : confirmLabel}
            </Button>
        </Modal.Footer>
    );

    return (
        <>
            {alert && <Alert variant={alert.variant} className="py-2 small mb-3" role="status">{alert.msg}</Alert>}

            <div className="d-flex align-items-center justify-content-between mb-3">
                <InputGroup style={{ maxWidth: 300 }}>
                    <InputGroup.Text className="bg-transparent border-end-0">
                        <Search size={14} className="text-muted" />
                    </InputGroup.Text>
                    <Form.Control placeholder="Search students…" value={search}
                        onChange={e => setSearch(e.target.value)} className="border-start-0 shadow-none" />
                </InputGroup>
                <Button variant="primary" size="sm" className="rounded-pill d-flex align-items-center gap-2"
                    onClick={() => { setForm({ language_preference: 'en' }); setModal('add'); }}>
                    <Plus size={14} /> Add Student
                </Button>
            </div>

            <Card className="faculty-card">
                <Table responsive hover className="mb-0 small">
                    <thead className="table-light">
                        <tr>
                            <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3 px-4">Student</th>
                            <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3">Status</th>
                            <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3">Language</th>
                            <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3">Last Login</th>
                            <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3 text-end pe-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={5} className="text-center py-4"><Spinner animation="border" size="sm" aria-hidden="true" /></td></tr>
                        ) : filtered.length === 0 ? (
                            <EmptyRow cols={5} icon={Users} message="No students found. Add your first student above." />
                        ) : filtered.map(s => (
                            <tr key={s.id}>
                                <td className="py-3 px-4">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center fw-bold text-primary"
                                            style={{ width: 34, height: 34, fontSize: '0.8rem', flexShrink: 0 }}>
                                            {(s.full_name || s.email || '?')[0].toUpperCase()}
                                        </div>
                                        <div>
                                            <div className="fw-semibold text-dark">{s.full_name || '—'}</div>
                                            <div className="x-small text-muted d-flex align-items-center gap-2 flex-wrap">
                                                <span>{s.email}</span>
                                                {s.telegram_id && (
                                                    <span title={`Telegram: ${s.telegram_id}${s.telegram_username ? ' @' + s.telegram_username : ''}`}
                                                        style={{ color: '#229ED9', fontWeight: 700, fontSize: '0.62rem' }}>
                                                        ✈ TG
                                                    </span>
                                                )}
                                                {s.phone_number && (
                                                    <span title={`WhatsApp: ${s.phone_number}`}
                                                        style={{ color: '#25D366', fontWeight: 700, fontSize: '0.62rem' }}>
                                                        📱 WA
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td className="py-3 align-middle"><StatusBadge status={s.status} /></td>
                                <td className="py-3 align-middle text-muted">{s.language_preference || 'en'}</td>
                                <td className="py-3 align-middle text-muted x-small">
                                    {s.last_login ? new Date(s.last_login).toLocaleDateString() : 'Never'}
                                </td>
                                <td className="py-3 pe-4 align-middle text-end">
                                    <div className="d-flex align-items-center justify-content-end gap-2">
                                        <Button variant="outline-secondary" size="sm" className="d-flex align-items-center gap-1 rounded-pill"
                                            onClick={() => { setSelected(s); setForm({ full_name: s.full_name, language_preference: s.language_preference, voice_preference: s.voice_preference, telegram_id: s.telegram_id || '', telegram_username: s.telegram_username || '', phone_number: s.phone_number || '' }); setModal('edit'); }}>
                                            <Pencil size={12} /> Edit
                                        </Button>
                                        <Button variant="outline-warning" size="sm" className="d-flex align-items-center gap-1 rounded-pill"
                                            onClick={() => { setSelected(s); setForm({ password: '' }); setModal('reset'); }}>
                                            <KeyRound size={12} /> Reset PW
                                        </Button>
                                        <Button variant="outline-danger" size="sm" className="d-flex align-items-center gap-1 rounded-pill"
                                            onClick={() => { setSelected(s); setModal('delete'); }}>
                                            <Trash2 size={12} /> Remove
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Card>

            <Modal show={modal === 'add'} onHide={() => setModal(null)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold fs-6">Add New Student</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <UInput label="Full Name" placeholder="Jane Doe" value={form.full_name || ''} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
                    <UInput label="University Email" type="email" placeholder="jane@university.edu" value={form.email || ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                    <UInput label="Temporary Password" type="password" placeholder="••••••••" value={form.password || ''} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Preferred Language</Form.Label>
                        <Form.Select value={form.language_preference || 'en'} onChange={e => setForm(f => ({ ...f, language_preference: e.target.value }))} className="shadow-none">
                            {LANGS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                        </Form.Select>
                    </Form.Group>
                    <hr className="my-3" style={{ borderColor: '#e2e8f0' }} />
                    <p className="x-small fw-bold text-muted text-uppercase mb-1" style={{ letterSpacing: '0.07em' }}>
                        📱 Messaging Platforms <span className="fw-normal text-muted">(optional)</span>
                    </p>
                    <p className="x-small text-muted mb-3">
                        Students can also self-link Telegram by sending /link to the bot.
                    </p>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Telegram ID (chat_id)</Form.Label>
                        <Form.Control className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent"
                            placeholder="e.g. 987654321"
                            value={form.telegram_id || ''}
                            onChange={e => setForm(f => ({ ...f, telegram_id: e.target.value }))} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Telegram Username</Form.Label>
                        <Form.Control className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent"
                            placeholder="studenthandle (without @)"
                            value={form.telegram_username || ''}
                            onChange={e => setForm(f => ({ ...f, telegram_username: e.target.value }))} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Phone Number (WhatsApp)</Form.Label>
                        <Form.Control className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent"
                            placeholder="+919876543210 (E.164 format)"
                            value={form.phone_number || ''}
                            onChange={e => setForm(f => ({ ...f, phone_number: e.target.value }))} />
                    </Form.Group>
                </Modal.Body>
                <ModalFooter confirmLabel="Add Student" />
            </Modal>

            <Modal show={modal === 'edit'} onHide={() => setModal(null)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold fs-6">Edit — {selected?.email}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <UInput label="Full Name" value={form.full_name || ''} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Preferred Language</Form.Label>
                        <Form.Select value={form.language_preference || 'en'} onChange={e => setForm(f => ({ ...f, language_preference: e.target.value }))} className="shadow-none">
                            {LANGS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Voice Preference</Form.Label>
                        <Form.Select value={form.voice_preference || 'nova'} onChange={e => setForm(f => ({ ...f, voice_preference: e.target.value }))} className="shadow-none">
                            {VOICES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                        </Form.Select>
                    </Form.Group>
                    <hr className="my-3" style={{ borderColor: '#e2e8f0' }} />
                    <p className="x-small fw-bold text-muted text-uppercase mb-1" style={{ letterSpacing: '0.07em' }}>
                        📱 Messaging Platforms
                    </p>
                    {selected?.telegram_id && !form.telegram_id && (
                        <div className="d-flex align-items-center justify-content-between p-2 rounded-3 mb-3"
                            style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                            <div>
                                <p className="x-small fw-semibold mb-0" style={{ color: '#15803d' }}>✈ Telegram Linked</p>
                                <p className="x-small text-muted mb-0">
                                    ID: {selected.telegram_id}
                                    {selected.telegram_username && ` · @${selected.telegram_username}`}
                                </p>
                            </div>
                            <Button size="sm" variant="outline-danger" className="rounded-pill d-flex align-items-center gap-1"
                                onClick={() => setForm(f => ({ ...f, telegram_id: '', telegram_username: '' }))}
                                title="Clear Telegram link — student will need to re-link">
                                <Unlink size={12} />Unlink
                            </Button>
                        </div>
                    )}
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Telegram ID (chat_id)</Form.Label>
                        <Form.Control className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent"
                            placeholder="e.g. 987654321 — or clear to unlink"
                            value={form.telegram_id || ''}
                            onChange={e => setForm(f => ({ ...f, telegram_id: e.target.value }))} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Telegram Username</Form.Label>
                        <Form.Control className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent"
                            placeholder="studenthandle (without @)"
                            value={form.telegram_username || ''}
                            onChange={e => setForm(f => ({ ...f, telegram_username: e.target.value }))} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Phone Number (WhatsApp)</Form.Label>
                        <Form.Control className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent"
                            placeholder="+919876543210 (E.164 format)"
                            value={form.phone_number || ''}
                            onChange={e => setForm(f => ({ ...f, phone_number: e.target.value }))} />
                    </Form.Group>
                </Modal.Body>
                <ModalFooter confirmLabel="Save Changes" />
            </Modal>

            <Modal show={modal === 'reset'} onHide={() => setModal(null)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold fs-6">Reset Password — {selected?.email}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <UInput label="New Password" type="password" placeholder="New temporary password" value={form.password || ''} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
                </Modal.Body>
                <ModalFooter confirmLabel="Reset Password" confirmVariant="warning" />
            </Modal>

            <Modal show={modal === 'delete'} onHide={() => setModal(null)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold fs-6 text-danger">Remove Student?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="small text-muted">
                        This will deactivate <strong>{selected?.email}</strong>. They will immediately lose access to AskOS. This can be reversed by editing the student's status.
                    </p>
                </Modal.Body>
                <ModalFooter confirmLabel="Remove Student" confirmVariant="danger" />
            </Modal>
        </>
    );
};

export default StudentsTab;
